import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Embers from "@/components/Embers";
import { ArrowRight, ArrowUpRight, Flame, Film, Clapperboard, Sparkles, Phone, Mail } from "lucide-react";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/6ff463c7-1a12-4c8a-9f0f-c3c87698bd81/images/3f766aa57f0a23501aecdab476a61c6005eef3506d44e075ecd3c0657656fb41.png";

// Bento layout spans for asymmetry (desktop)
const SPANS = [
  "md:col-span-2 md:row-span-2",
  "md:col-span-1 md:row-span-1",
  "md:col-span-1 md:row-span-1",
  "md:col-span-1 md:row-span-1",
  "md:col-span-1 md:row-span-1",
  "md:col-span-2 md:row-span-1",
];

function ProjectCard({ project, index }) {
  const span = SPANS[index % SPANS.length];
  const big = span.includes("row-span-2");
  return (
    <Link
      to={`/projects/${project.slug}`}
      data-testid={`project-card-${project.slug}`}
      className={`group relative overflow-hidden rounded-sm border border-white/10 bg-[#141414] ${span} ${
        big ? "min-h-[320px] md:min-h-[560px]" : "min-h-[260px]"
      }`}
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <img
        src={project.banner || project.poster}
        alt={project.title}
        className={`absolute inset-0 w-full h-full object-cover ${project.banner ? "object-top" : ""} opacity-70 transition-transform duration-700 group-hover:scale-105 group-hover:opacity-90`}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
      <div className="absolute top-4 left-4 flex items-center gap-2">
        <span
          className="text-[10px] tracking-[0.15em] uppercase font-bold px-2.5 py-1 rounded-sm backdrop-blur-md border"
          style={{ color: project.accent, borderColor: `${project.accent}55`, background: "rgba(0,0,0,0.4)" }}
        >
          {project.status}
        </span>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <p className="text-xs tracking-[0.18em] uppercase font-semibold text-white/55 mb-1.5">{project.format}</p>
        <h3 className={`font-display font-extrabold tracking-tight uppercase leading-none ${big ? "text-3xl md:text-5xl" : "text-2xl"}`}>
          {project.title}
        </h3>
        <p className="text-white/60 text-sm mt-2 max-w-md line-clamp-2">{project.logline}</p>
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold mt-4 text-white group-hover:text-[#E8954A] transition-colors">
          Explore project <ArrowUpRight className="w-4 h-4" />
        </span>
      </div>
    </Link>
  );
}

const STATS_META = [
  { icon: Film, label: "Films", match: (f) => /film/i.test(f) },
  { icon: Clapperboard, label: "Series", match: (f) => /series/i.test(f) && !/reality/i.test(f) },
  { icon: Sparkles, label: "Reality Formats", match: (f) => /reality/i.test(f) },
];

export default function Home() {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/projects").then((res) => setProjects(res.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/60" />
        <Embers />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-28 pb-16">
          <div className="flex items-center gap-2.5 mb-6 fade-up">
            <Flame className="w-4 h-4 text-[#E8954A]" />
            <span className="text-xs tracking-[0.25em] uppercase font-bold text-white/60">Independent Film & Television Studio</span>
          </div>
          <h1 className="font-display font-black uppercase tracking-tighter leading-[0.92] text-5xl sm:text-7xl lg:text-8xl max-w-4xl fade-up" style={{ animationDelay: "0.1s" }}>
            Stories forged <br />
            <span className="text-[#E8954A]">in iron</span> and fire.
          </h1>
          <p className="text-white/70 text-lg md:text-xl max-w-xl mt-7 leading-relaxed fade-up" style={{ animationDelay: "0.2s" }}>
            Iron Fire Productions develops bold screenplays, original series, and groundbreaking reality formats — built to find their audience and their producers.
          </p>
          <div className="flex flex-wrap gap-4 mt-10 fade-up" style={{ animationDelay: "0.3s" }}>
            <a
              href="#work"
              data-testid="hero-view-work"
              className="bg-white text-black font-semibold px-7 py-3.5 rounded-sm hover:bg-[#E8954A] transition-all duration-300 hover:-translate-y-1 flex items-center gap-2"
            >
              View the Slate <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#studio"
              data-testid="hero-about"
              className="border border-white/25 text-white font-semibold px-7 py-3.5 rounded-sm hover:bg-white/5 transition-all duration-300 hover:-translate-y-1"
            >
              About the Studio
            </a>
          </div>
        </div>
      </section>

      {/* WORK / BENTO GRID */}
      <section id="work" className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E8954A] mb-3">The Slate</p>
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter uppercase max-w-2xl">
              Projects in development
            </h2>
          </div>
          <p className="text-white/55 max-w-sm text-sm leading-relaxed">
            A growing collection of feature films, series, and reality concepts. Select a project to read more and request materials.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 auto-rows-[260px] gap-4 grid-flow-row-dense">
          {projects.map((p, i) => (
            <ProjectCard key={p.slug} project={p} index={i} />
          ))}
        </div>
      </section>

      {/* STUDIO */}
      <section id="studio" className="relative z-10 bg-[#0E0E0E] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E8954A] mb-3">The Studio</p>
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter uppercase leading-none mb-7">
              Built for the next generation of storytelling
            </h2>
            <p className="text-white/65 leading-relaxed mb-5">
              Iron Fire Productions is an independent studio creating original intellectual property across film, television, and interactive reality. For years we've been quietly developing a slate of distinct, commercially-minded ideas — from war dramas and science fiction to audience-driven reality formats.
            </p>
            <p className="text-white/65 leading-relaxed">
              Now we're opening the vault. Each project is available to verified producers, directors, and industry partners who want to read deeper, see the full pitch, and help bring these worlds to the screen.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {STATS_META.map((s) => (
              <div key={s.label} className="flex items-center gap-5 border border-white/10 rounded-sm p-6 bg-[#141414]">
                <s.icon className="w-8 h-8 text-[#E8954A]" strokeWidth={1.6} />
                <div>
                  <p className="font-display text-4xl font-black leading-none">{projects.filter((p) => s.match(p.format)).length}</p>
                  <p className="text-white/55 text-sm mt-1">{s.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDERS */}
      <section id="founders" className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="max-w-3xl mb-16">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E8954A] mb-3">The Founders</p>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter uppercase leading-none mb-7">
            Two storytellers, one fire
          </h2>
          <p className="text-white/65 leading-relaxed">
            Greg Demirjian and Angela Sparks Beddoe first met in college. Years later, reconnecting at a college reunion, they discovered they shared the same instincts about scripts, reality-TV concepts, and the art of producing. They began building their work together — and Iron Fire Productions was born.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Greg */}
          <div data-testid="founder-greg" className="border border-white/10 rounded-sm bg-[#141414] overflow-hidden flex flex-col sm:flex-row">
            <div className="sm:w-44 shrink-0 bg-black">
              <img
                src="https://customer-assets.emergentagent.com/job_ironfire-studio/artifacts/mbyhpeof_greg%20bio%20pic2.jpg"
                alt="Greg Demirjian"
                className="w-full h-56 sm:h-full object-cover"
              />
            </div>
            <div className="p-6">
              <h3 className="font-display text-2xl font-bold tracking-tight">Greg Demirjian</h3>
              <p className="text-xs uppercase tracking-wider text-[#E8954A] mb-3">Co-Founder · Director & Producer</p>
              <p className="text-white/60 text-sm leading-relaxed mb-4">
                Greg's background spans directing, film editing, special effects, and holographic presentations — a visual storyteller who shapes the look, feel, and craft behind Iron Fire's slate.
              </p>
              <a href="tel:+12424310833" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#E8954A] transition-colors" data-testid="greg-phone">
                <Phone className="w-4 h-4" /> 242-431-0833
              </a>
            </div>
          </div>

          {/* Angela */}
          <div data-testid="founder-angela" className="border border-white/10 rounded-sm bg-[#141414] overflow-hidden flex flex-col sm:flex-row">
            <div className="sm:w-44 shrink-0 bg-black">
              <img
                src="https://customer-assets.emergentagent.com/job_ironfire-studio/artifacts/d2olp0w9_Screen%20shot%202011-11-28%20at%205.55.49%20PM.png"
                alt="Angela Sparks Beddoe"
                className="w-full h-56 sm:h-full object-cover object-top"
              />
            </div>
            <div className="p-6">
              <h3 className="font-display text-2xl font-bold tracking-tight">Angela Sparks Beddoe</h3>
              <p className="text-xs uppercase tracking-wider text-[#E8954A] mb-3">Co-Founder · Writer & Producer</p>
              <p className="text-white/60 text-sm leading-relaxed mb-4">
                Angela's background is in writing, producing, and communications, with leadership roles across the field — including founder and publisher of Her Life magazine. She is the writer-producer behind Walkers.
              </p>
              <a href="tel:+15185271837" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#E8954A] transition-colors" data-testid="angela-phone">
                <Phone className="w-4 h-4" /> 518-527-1837
              </a>
            </div>
          </div>
        </div>
      </section>
      <section id="contact" className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
        <Flame className="w-10 h-10 text-[#E8954A] mx-auto mb-7" strokeWidth={1.8} />
        <h2 className="font-display text-4xl sm:text-6xl font-black tracking-tighter uppercase max-w-3xl mx-auto leading-none">
          Let's build something unforgettable
        </h2>
        <p className="text-white/60 max-w-xl mx-auto mt-6 leading-relaxed">
          Producers, directors, financiers and partners — explore the slate and request a screenplay or full pitch on any project. We respond to every serious inquiry.
        </p>
        <div className="flex flex-wrap justify-center gap-4 mt-10">
          <a
            href="#work"
            data-testid="contact-view-projects"
            className="bg-white text-black font-semibold px-8 py-4 rounded-sm hover:bg-[#E8954A] transition-all duration-300 hover:-translate-y-1 flex items-center gap-2"
          >
            Browse Projects <ArrowRight className="w-4 h-4" />
          </a>
          <a
            href="mailto:greg@ironfireproductions.com"
            data-testid="contact-email"
            className="border border-white/25 text-white font-semibold px-8 py-4 rounded-sm hover:bg-white/5 transition-all duration-300 hover:-translate-y-1 flex items-center gap-2"
          >
            <Mail className="w-4 h-4" /> greg@ironfireproductions.com
          </a>
        </div>
        <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 mt-8 text-sm text-white/55">
          <a href="tel:+12424310833" className="inline-flex items-center gap-2 hover:text-white transition-colors" data-testid="contact-greg-phone">
            <Phone className="w-4 h-4 text-[#E8954A]" /> Greg Demirjian · 242-431-0833
          </a>
          <a href="tel:+15185271837" className="inline-flex items-center gap-2 hover:text-white transition-colors" data-testid="contact-angela-phone">
            <Phone className="w-4 h-4 text-[#E8954A]" /> Angela Sparks Beddoe · 518-527-1837
          </a>
        </div>
      </section>
    </div>
  );
}
